import { useCallback, useLayoutEffect } from "react";

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { posToDOMRect } from "@tiptap/react";
import { observer } from "mobx-react-lite";

import { useEditorPopover } from "./useEditorPopover";

import type { DocumentModel } from "@/models/DocumentModel";

export const EditorPopoverView = observer(function EditorPopoverView({
  doc,
}: {
  doc: DocumentModel;
}) {
  const editor = doc.editor.tiptap;

  const { isOpen, state, close } = useEditorPopover(editor);

  const closeAndFocus = useCallback(() => {
    close();
    doc.focus();
  }, [close, doc]);

  const anchoredToBlock =
    !!editor && !!state && !editor.state.doc.resolve(state.from).parent.inlineContent;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) {
        closeAndFocus();
      }
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    // A block taller than the viewport fits neither side; overlap it instead of flipping above
    middleware: anchoredToBlock
      ? [offset({ mainAxis: 6, crossAxis: -6 }), shift({ padding: 8, crossAxis: true })]
      : [offset({ mainAxis: 6, crossAxis: -6 }), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
  });
  const { getFloatingProps } = useInteractions([dismiss]);

  useLayoutEffect(() => {
    if (!editor || !state) {
      return;
    }

    refs.setReference({
      getBoundingClientRect() {
        const rect = posToDOMRect(editor.view, state.from, state.to);
        if (!anchoredToBlock) {
          return rect;
        }

        // The block's DOM includes the line-number gutter; align with the line content instead
        const blockDom = editor.view.nodeDOM(state.from);
        const content =
          blockDom instanceof HTMLElement && blockDom.querySelector("[data-line-content]");
        if (!content) {
          return rect;
        }

        const { left } = content.getBoundingClientRect();
        return new DOMRect(left, rect.top, rect.right - left, rect.height);
      },
    });
  }, [refs, editor, state, anchoredToBlock]);

  if (!isOpen || !state) {
    return null;
  }

  return (
    <FloatingPortal>
      <div ref={refs.setFloating} style={floatingStyles} className="z-50" {...getFloatingProps()}>
        {state.render(closeAndFocus)}
      </div>
    </FloatingPortal>
  );
});
